import {type ReactNode} from 'react';
import {Table2} from 'lucide-react';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle
} from '@kroll-resolver-crisp/ui-components-react';
import {formatCell} from './format';
import {type ReportTableBlock} from '@/api/reporting';
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@/components/ui/table';
import {TablePanel} from '@/components/ui/table-panel';
import {cn} from '@/lib/utils';

// Tables sit on the bordered panel the QA and admin tables use, not on a Card, so a report's table
// and the app's other tables are the same thing.
export function TableBlock({
  block,
  actions
}: {
  readonly block: ReportTableBlock;
  readonly actions?: ReactNode;
}) {
  return (
    <section data-block-id={block.id} data-block-type={block.type}>
      <div className="mb-3 flex items-start justify-between gap-4">
        <div>
          <h3 className="font-heading text-sm font-medium">{block.title}</h3>
          {block.description ? (
            <p className="text-muted-foreground text-xs">{block.description}</p>
          ) : null}
        </div>
        {actions}
      </div>
      <TablePanel>
        <Table>
          <TableHeader>
            <TableRow>
              {block.columns.map((column) => (
                <TableHead
                  key={column.field}
                  className={cn(column.align === 'right' && 'text-right')}>
                  {column.label}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {block.rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={block.columns.length} className="p-0">
                  <Empty className="py-10">
                    <EmptyHeader>
                      <EmptyMedia variant="icon">
                        <Table2 />
                      </EmptyMedia>
                      <EmptyTitle>No rows</EmptyTitle>
                      <EmptyDescription>Nothing matched this range.</EmptyDescription>
                    </EmptyHeader>
                  </Empty>
                </TableCell>
              </TableRow>
            ) : null}
            {block.rows.map((row, index) => (
              // Rows carry no key of their own; a report's rows are read-only and replaced wholesale.
              // eslint-disable-next-line react/no-array-index-key
              <TableRow key={index}>
                {block.columns.map((column) => (
                  <TableCell
                    key={column.field}
                    className={cn(
                      column.align === 'right' && 'text-right tabular-nums',
                      column.format === undefined && 'font-medium'
                    )}>
                    {formatCell(row[column.field] ?? null, column.format)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TablePanel>
    </section>
  );
}
